/* Progressive enhancement. Single-item forms work without this script.
   Add-ons use real variant IDs and Shopify's authoritative cart prices/availability.
   No automatic retries: an uncertain response can represent a partially completed request. */
(() => {
  'use strict';
  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'nl', {style:'currency', currency: currency || 'EUR'}).format(cents / 100);
  function initProducts(scope = document) {
    const roots = [...scope.querySelectorAll('[data-kcx-product]')];
    if (scope.matches?.('[data-kcx-product]')) roots.push(scope);
    roots.forEach(root => {
      if (root.dataset.kcxReady) return;
      const form = root.querySelector('[data-kcx-form]');
      if (!form) return;
      let labels;
      try { labels = JSON.parse(root.querySelector('[data-kcx-labels]').textContent); } catch (_) { return; }
      root.dataset.kcxReady = 'true';
      const radios = [...form.querySelectorAll('input[type="radio"][data-kcx-variant]')];
      const select = form.querySelector('[data-kcx-select]');
      const hidden = form.querySelector('input[type="hidden"][data-kcx-variant]');
      const addon = form.querySelector('[data-kcx-addon]');
      const extra = form.querySelector('[data-kcx-extra]');
      const submit = form.querySelector('[data-kcx-add]');
      const submitLabel = form.querySelector('[data-kcx-add-label]');
      const errors = form.querySelector('[data-kcx-error]');
      const current = () => select ? select.options[select.selectedIndex] : (radios.find(input => input.checked) || hidden);
      const hasExtra = () => Boolean(extra && extra.value && current()?.value === root.dataset.completeId);
      let inFlight = false;
      const render = (updateUrl = false) => {
        const variant = current();
        if (!variant) return;
        const isPlate = variant.value === root.dataset.plateId;
        const isComplete = variant.value === root.dataset.completeId;
        if (extra && !isComplete) extra.value = '';
        if (addon) addon.hidden = !isComplete;
        radios.forEach(input => input.closest('.kp-offer')?.classList.toggle('is-selected', input.checked));
        const price = Number(variant.dataset.price);
        root.querySelector('[data-kcx-price]').textContent = variant.dataset.priceText || money(price, root.dataset.currency);
        const compare = root.querySelector('[data-kcx-compare]');
        const comparePrice = Number(variant.dataset.compare || 0);
        compare.hidden = !(comparePrice > price);
        if (!compare.hidden) compare.textContent = variant.dataset.compareText || money(comparePrice, root.dataset.currency);
        if (root.dataset.plateId) {
          root.querySelectorAll('[data-kcx-includes],[data-kcx-box]').forEach(el => { el.textContent = isPlate ? labels.plate : labels.complete; });
        }
        let total = price;
        if (extra) {
          const selected = extra.options[extra.selectedIndex];
          const selectedExtra = hasExtra();
          if (selectedExtra) total += Number(selected.dataset.price);
          form.querySelector('[data-kcx-extra-preview]').hidden = !selectedExtra;
          form.querySelector('[data-kcx-total-wrap]').hidden = !selectedExtra;
          if (selectedExtra) {
            const image = form.querySelector('[data-kcx-extra-image]');
            image.hidden = !selected.dataset.image;
            if (selected.dataset.image) image.src = selected.dataset.image;
            else image.removeAttribute('src');
            image.alt = selected.dataset.title;
            form.querySelector('[data-kcx-extra-title]').textContent = selected.dataset.title;
            form.querySelector('[data-kcx-total]').textContent = money(total, root.dataset.currency);
          }
        }
        root.querySelectorAll('[data-kcx-mobile-price]').forEach(el => el.textContent = money(total, root.dataset.currency));
        submit.disabled = inFlight || variant.dataset.available !== 'true';
        submitLabel.textContent = inFlight ? labels.working : (submit.disabled ? labels.unavailable : (hasExtra() ? labels.addBoth : labels.add));
        if (updateUrl && window.history?.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.value);
          window.history.replaceState(window.history.state, '', url);
        }
      };
      radios.forEach(input => input.addEventListener('change', () => render(true)));
      select?.addEventListener('change', () => render(true));
      extra?.addEventListener('change', () => render(false));
      form.addEventListener('submit', async event => {
        if (inFlight) { event.preventDefault(); return; }
        if (!hasExtra()) return; // Native Shopify single-item submission.
        event.preventDefault();
        const variant = current();
        if (!variant || variant.dataset.available !== 'true') return;
        errors.hidden = true;
        inFlight = true;
        render();
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
          const route = window.Shopify?.routes?.root || window.routes?.rootUrl || '/';
          const response = await fetch(`${route}cart/add.js`, {
            method: 'POST', credentials: 'same-origin',
            headers: {'Content-Type':'application/json', 'Accept':'application/json'},
            body: JSON.stringify({items:[{id:Number(variant.value),quantity:1},{id:Number(extra.value),quantity:1}]}),
            signal: controller.signal
          });
          const result = await response.json();
          if (!response.ok || result.status) throw new Error('cart_request_not_confirmed');
          // Keep native cart/checkout pricing authoritative, including active discounts.
          window.location.assign(`${route}cart`);
        } catch (_) {
          errors.querySelector('[data-kcx-error-text]').textContent = labels.error;
          errors.hidden = false;
          errors.setAttribute('tabindex','-1');
          errors.focus();
        } finally {
          window.clearTimeout(timeout);
          inFlight = false;
          render();
        }
      });
      const sticky = root.querySelector('[data-kcx-sticky]');
      if (sticky && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => sticky.hidden = entries[0].isIntersecting, {threshold:0.6});
        observer.observe(submit);
        root._kcxObserver = observer;
      }
      render();
    });
  }
  function keepOverlayClear() {
    const overlay = document.querySelector('.PageOverlay');
    const obscured = overlay && (overlay.classList.contains('is-visible') || overlay.classList.contains('is-active'));
    document.querySelectorAll('[data-kcx-sticky]').forEach(el => el.classList.toggle('is-obscured', Boolean(obscured)));
  }
  let shippingTimer;
  let shippingInFlight = false;
  let shippingQueued = false;
  async function refreshShipping() {
    if (!document.querySelector('[data-kcx-shipping]')) return;
    if (shippingInFlight) { shippingQueued = true; return; }
    shippingInFlight = true;
    try {
      const route = window.Shopify?.routes?.root || window.routes?.rootUrl || '/';
      const response = await fetch(`${route}cart.js`, {credentials:'same-origin', headers:{Accept:'application/json'}});
      if (!response.ok) return;
      const cart = await response.json();
      document.querySelectorAll('[data-kcx-shipping]').forEach(block => {
        const copy = key => block.querySelector(`[data-copy="${key}"]`)?.textContent.trim() || '';
        const eligible = ['NL','BE'].includes(block.dataset.country) && cart.currency === 'EUR' && cart.item_count > 0 && cart.requires_shipping;
        let bar = block.querySelector('progress');
        const message = block.querySelector('[data-kcx-shipping-message]');
        if (!message) return;
        if (eligible) {
          const remaining = Math.max(0, Number(block.dataset.threshold) - Number(cart.total_price));
          message.textContent = remaining > 0 ? `${copy('remaining')} ${money(remaining, cart.currency)} ${copy('suffix')}` : copy('reached');
          if (!bar) { bar = document.createElement('progress'); bar.max = 100; bar.setAttribute('aria-label', copy('short')); message.after(bar); }
          bar.hidden = false;
          bar.value = Math.max(0, Math.min(100, Number(cart.total_price) / Number(block.dataset.threshold) * 100));
          block.querySelector('[data-kcx-shipping-note]').textContent = copy('note');
        } else {
          message.textContent = copy('short');
          if (bar) bar.hidden = true;
          block.querySelector('[data-kcx-shipping-note]').textContent = copy('other');
        }
      });
    } catch (_) { /* Keep server-rendered shipping text; checkout remains authoritative. */ }
    finally { shippingInFlight = false; if (shippingQueued) { shippingQueued = false; scheduleShipping(); } }
  }
  const scheduleShipping = () => { window.clearTimeout(shippingTimer); shippingTimer = window.setTimeout(refreshShipping, 150); };
  function init() {
    initProducts(); keepOverlayClear();
    new MutationObserver(records => {
      const changed = records.some(record => {
        const target = record.target.nodeType === 1 ? record.target : record.target.parentElement;
        if (!target || target.closest('[data-kcx-shipping]')) return false;
        if (target.closest('.Cart__ItemList,.Cart__Footer,.Drawer__Footer,.Cart__Total')) return true;
        return [...record.addedNodes].some(node => node.nodeType === 1 && (node.matches?.('[data-kcx-shipping]') || node.querySelector?.('[data-kcx-shipping]')));
      });
      if (changed) scheduleShipping();
    }).observe(document.body,{subtree:true,childList:true,characterData:true});
    document.addEventListener('cart:refresh', scheduleShipping);
    window.addEventListener('pageshow', scheduleShipping);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, {once:true}); else init();
  document.addEventListener('shopify:section:load', event => initProducts(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-kcx-product]').forEach(root => root._kcxObserver?.disconnect()));
  window.addEventListener('pageshow', () => { initProducts(); keepOverlayClear(); });
  document.addEventListener('DOMContentLoaded', () => {
    const overlay = document.querySelector('.PageOverlay');
    if (overlay) new MutationObserver(keepOverlayClear).observe(overlay,{attributes:true,attributeFilter:['class']});
  },{once:true});
})();
